import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { canDelete } from "../utils/permissions";
import { ROUTES, editUserPath, editAccessRequestPath } from "../config/routes";

const API_BASE = API_ORIGIN;
const PAGE_SIZE = 10;
const AVATAR_COLORS = ["#E97031", "#155F82", "#186B23", "#7C3AED", "#DB2777", "#0EA5E9", "#EA580C"];

/* Inline icons, same approach/style as ProjectList.jsx's action icons */
const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="10" y1="11" x2="10" y2="17" strokeLinecap="round" />
    <line x1="14" y1="11" x2="14" y2="17" strokeLinecap="round" />
  </svg>
);

function initialsForName(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("");
}

function colorForName(name) {
  const str = name || "?";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// Backend sends a naive local datetime as an ISO string with no "Z" --
// new Date() parses that as local time already, so no timezone shift.
function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} – ${hh}:${mm}`;
}

// Sort icon convention matches ProjectList.jsx's sortIcon() exactly --
// muted "⇅" when this column isn't the active sort, a plain "↑"/"↓"
// once it is -- rather than the old always-both-triangles rendering,
// so this table's headers look and behave the same as the rest of the
// app instead of like a leftover custom component.
function SortHeader({ label, columnKey, sortKey, sortDir, onSort, className }) {
  const isActive = sortKey === columnKey;
  return (
    <th className={className} role="button" onClick={() => onSort(columnKey)}>
      {label}{" "}
      {isActive ? (
        <span className="ms-1">{sortDir === "asc" ? "↑" : "↓"}</span>
      ) : (
        <span className="text-muted ms-1">⇅</span>
      )}
    </th>
  );
}

export default function UsersList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Delete-user confirmation -- replaces the old window.confirm() browser
  // popup with the same teal styled-modal convention used elsewhere in
  // the app (see ProfilePage.jsx's "Profile updated successfully" modal).
  // Holds the user object pending confirmation, or null when hidden.
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);

  // Same idea, for deleting an access request from the Access Request tab
  // -- holds the request object pending confirmation, or null when hidden.
  const [confirmDeleteRequest, setConfirmDeleteRequest] = useState(null);

  const [activeRole, setActiveRole] = useState("All");
  const [sortKey, setSortKey] = useState("Created");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [viewingUser, setViewingUser] = useState(null);

  // Client-side search box in the page header (matches
  // CET-HTML-Code/users.html's "app-search" input) -- filters whichever
  // table is currently showing, by name (Users tab: UserName/RoleName/
  // Email, Access Request tab: Name/Department/CompanyName).
  const [search, setSearch] = useState("");

  // --- Access Request tab (Account > User > "Access Request") ---
  // Separate state from the Users tab above -- different data shape, and
  // its own pagination so switching tabs doesn't reset either one. View/
  // Edit/Delete are the only row actions now -- Accept/Reject moved into
  // the View/Edit page's own Status dropdown (EditAccessRequestForm.jsx).
  const [accessRequests, setAccessRequests] = useState([]);
  const [arLoading, setArLoading] = useState(true);
  const [arError, setArError] = useState("");
  const [arActingId, setArActingId] = useState(null); // request currently being deleted
  const [arPage, setArPage] = useState(1);

  const isAccessRequestTab = activeRole === "Access Request";

  const loadUsers = () => {
    setLoading(true);
    setError("");
    authFetch(`${API_BASE}/users`)
      .then((res) => res.json())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setError("Could not load users."))
      .finally(() => setLoading(false));
  };

  const loadAccessRequests = () => {
    setArLoading(true);
    setArError("");
    authFetch(`${API_BASE}/access-requests`)
      .then((res) => res.json())
      .then((data) => setAccessRequests(Array.isArray(data) ? data : []))
      .catch(() => setArError("Could not load access requests."))
      .finally(() => setArLoading(false));
  };

  useEffect(() => {
    loadUsers();
    loadAccessRequests();
    authFetch(`${API_BASE}/roles`)
      .then((res) => res.json())
      .then((data) => setRoles(Array.isArray(data) ? data : []))
      .catch(() => setRoles([]));
  }, []);

  useEffect(() => {
    setPage(1);
    setArPage(1);
  }, [activeRole, search]);

  const toggleAccessStatus = async (user) => {
    const nextEnabled = !user.IsEnabled;
    setTogglingId(user.id);
    setError("");
    try {
      const res = await authFetch(`${API_BASE}/users/${user.id}/access-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ IsEnabled: nextEnabled }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to update access status.");
        return;
      }

      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, IsEnabled: nextEnabled } : u)));
    } catch (err) {
      setError("Network error while updating access status.");
    } finally {
      setTogglingId(null);
    }
  };

  // FIX: was a single window.confirm() call that did the delete inline on
  // OK -- replaced with the same teal styled-modal confirmation used
  // across the app (rendered below, near the other modals) so this looks
  // like the rest of the UI instead of a bare browser popup. handleDelete
  // now only opens that modal; confirmDeleteUserAction (wired to its
  // "Delete" button) does the actual work the old inline code did.
  const handleDelete = (user) => {
    setConfirmDeleteUser(user);
  };

  const cancelDeleteUser = () => setConfirmDeleteUser(null);

  const confirmDeleteUserAction = async () => {
    const user = confirmDeleteUser;
    if (!user) return;
    setConfirmDeleteUser(null);
    setDeletingId(user.id);
    setError("");
    try {
      const res = await authFetch(`${API_BASE}/users/${user.id}`, { method: "DELETE" });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to delete user.");
        return;
      }

      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (err) {
      setError("Network error while deleting the user.");
    } finally {
      setDeletingId(null);
    }
  };

  // Same teal styled-modal convention as confirmDeleteUser above --
  // replaces the old window.confirm() browser popup for this action too.
  // handleDeleteRequest just opens the modal now; confirmDeleteRequestAction
  // (wired to its "Delete" button) does the actual DELETE call.
  const handleDeleteRequest = (req) => {
    setConfirmDeleteRequest(req);
  };

  const cancelDeleteRequest = () => setConfirmDeleteRequest(null);

  const confirmDeleteRequestAction = async () => {
    const req = confirmDeleteRequest;
    if (!req) return;
    setConfirmDeleteRequest(null);

    setArError("");
    setArActingId(req.AccessRequestID);
    try {
      const res = await authFetch(`${API_BASE}/access-requests/${req.AccessRequestID}`, { method: "DELETE" });
      const data = await res.json();

      if (!res.ok) {
        setArError(data.error || "Failed to delete the access request.");
        return;
      }
      setAccessRequests((prev) => prev.filter((r) => r.AccessRequestID !== req.AccessRequestID));
    } catch (err) {
      setArError("Network error while deleting the access request.");
    } finally {
      setArActingId(null);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortValue = (u, key) => {
    switch (key) {
      case "Name":
        return (u.UserName || "").toLowerCase();
      case "Role":
        return (u.RoleName || "").toLowerCase();
      case "Created":
        return u.CreatedAt ? new Date(u.CreatedAt).getTime() : 0;
      case "Account":
        return u.Status || "";
      case "Access":
        return u.IsEnabled ? 1 : 0;
      default:
        return "";
    }
  };

  // "Access Request" is appended as a fixed extra tab alongside the
  // dynamic role tabs -- it doesn't filter the Users table, it swaps to
  // an entirely different table below (see isAccessRequestTab).
  //
  // The stale duplicate role that used to sit alongside "Contributor"
  // has been deleted from the Role table, so every tab now maps 1:1 to
  // a real role -- no hidden-tab filtering is needed any more. The
  // tabs still follow a fixed order (View, Contributor, Admin, Super
  // Admin) instead of whatever order /roles happens to return.
  const ROLE_TAB_ORDER = ["View", "Contributor", "Admin", "Super Admin"];

  const orderedRoleNames = [
    ...ROLE_TAB_ORDER.filter((name) => roles.some((r) => r.RoleName === name)),
    ...roles.filter((r) => !ROLE_TAB_ORDER.includes(r.RoleName)).map((r) => r.RoleName)
  ];
  const roleTabs = ["All", ...orderedRoleNames, "Access Request"];

  const roleFiltered = activeRole === "All" || isAccessRequestTab ? users : users.filter((u) => u.RoleName === activeRole);

  const searchQuery = search.trim().toLowerCase();
  const filtered = !searchQuery
    ? roleFiltered
    : roleFiltered.filter((u) =>
        [u.UserName, u.RoleName, u.Email].some((v) => (v || "").toLowerCase().includes(searchQuery))
      );

  const sorted = [...filtered].sort((a, b) => {
    const va = sortValue(a, sortKey);
    const vb = sortValue(b, sortKey);
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const totalEntries = sorted.length;
  const totalPages = Math.max(Math.ceil(totalEntries / PAGE_SIZE), 1);
  const currentPage = Math.min(page, totalPages);
  const startIndex = totalEntries === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalEntries);
  const pageRows = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  // Access requests come back from the backend already ordered by
  // RequestedAt DESC (see list_access_requests() in app.py) -- just
  // paginate client-side, same PAGE_SIZE as the Users tab.
  const arFiltered = !searchQuery
    ? accessRequests
    : accessRequests.filter((r) =>
        [r.Name, r.Department, r.CompanyName].some((v) => (v || "").toLowerCase().includes(searchQuery))
      );
  const arTotalEntries = arFiltered.length;
  const arTotalPages = Math.max(Math.ceil(arTotalEntries / PAGE_SIZE), 1);
  const arCurrentPage = Math.min(arPage, arTotalPages);
  const arStartIndex = arTotalEntries === 0 ? 0 : (arCurrentPage - 1) * PAGE_SIZE + 1;
  const arEndIndex = Math.min(arCurrentPage * PAGE_SIZE, arTotalEntries);
  const arPageRows = arFiltered.slice((arCurrentPage - 1) * PAGE_SIZE, arCurrentPage * PAGE_SIZE);
  const arPageNumbers = Array.from({ length: arTotalPages }, (_, i) => i + 1);

  return (
    <div className="container-fluid px-0">
      {/* Page Title & Header Actions -- matches CET-HTML-Code/users.html:
          "Manage Users" on the left, Add User + Search on the right.
          Refresh is kept as a small icon button (the theme reference
          doesn't have one -- table refresh is only ever triggered by
          the actions below it -- but this app's Users tab and Access
          Request tab are two independent live datasets fetched once on
          mount, so a manual reload is real, useful functionality worth
          keeping without disturbing the header's layout). */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 my-2">
        {/* Per request, this is now a breadcrumb trail ("Cost
            Estimation / {page}"), same pattern and color (#02bc9c,
            16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
            -- replaces the earlier #788CA5 bordered/uppercase title
            treatment. */}
        <nav aria-label="breadcrumb" className="d-inline-block">
          <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
          <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
          <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
            Manage Users
          </span>
        </nav>
        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-icon btn-sm"
            title="Refresh"
            onClick={isAccessRequestTab ? loadAccessRequests : loadUsers}
          >
            <i className="ti ti-refresh fs-lg" />
          </button>
          {!isAccessRequestTab && (
            <Link to={ROUTES.ACCOUNT_USERS_CREATE} className="btn btn-teal px-4 py-2 text-uppercase fw-semibold">
              Add User
            </Link>
          )}
          <div className="app-search">
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <i className="ti app-search-icon text-muted ti-search" />
          </div>
        </div>
      </div>

      {isAccessRequestTab
        ? arError && <div className="alert alert-danger">{arError}</div>
        : error && <div className="alert alert-danger">{error}</div>}

      {/* Main Card Container -- tabs in the card header, table in the
          body, "Showing X to Y of Z" + pagination in the footer, same
          structure as the theme reference. */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-light border-bottom-0 p-0">
          <div className="tabs-wrapper bg-light p-1">
            <ul className="nav nav-pills custom-role-tabs" role="tablist">
              {roleTabs.map((tab) => (
                <li className="nav-item" role="presentation" key={tab}>
                  <button
                    type="button"
                    className={`nav-link${activeRole === tab ? " active" : ""}`}
                    onClick={() => setActiveRole(tab)}
                  >
                    {tab}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {!isAccessRequestTab && (
          <>
            <div className="table-responsive">
              {/* users-list-table (new, scoped) -- per request, same
                  treatment as RateCard.jsx/ProjectList.jsx: row background
                  #f2f2f2 @ 38% Transparency, hover #02bc9c @ 95%
                  Transparency, via themeOverrides.css. Applied to both this
                  table and the Access Request tab's table below (same
                  page), rather than changing .table-custom/.table-hover
                  directly, since those are shared by ManageRoles.jsx /
                  RoleMatrix.jsx / AIAssistant.jsx / EmailConfiguration.jsx /
                  ProjectList.jsx and shouldn't change color there too. */}
              <table className="align-middle mb-0 table table-custom table-hover users-list-table text-nowrap w-100">
                <thead className="bg-light border-bottom border-top">
                  <tr className="text-dark fw-bold">
                    <SortHeader label="Name" columnKey="Name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-3 text-start" />
                    <SortHeader label="Role" columnKey="Role" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-3 text-center" />
                    <SortHeader label="Date Created" columnKey="Created" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-3 text-center" />
                    <SortHeader label="Account Status" columnKey="Account" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-3 text-center" />
                    <SortHeader label="Access Status" columnKey="Access" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-3 text-center" />
                    <th className="px-3 py-3 text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">Loading...</td>
                    </tr>
                  ) : pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">No users found.</td>
                    </tr>
                  ) : (
                    pageRows.map((u) => (
                      <tr key={u.id}>
                        <td className="px-3 text-start">
                          <div className="d-flex align-items-center gap-2 justify-content-start">
                            {u.ProfileImage ? (
                              <img
                                className="user-avatar"
                                src={`${API_BASE}/profile_photo/${u.ProfileImage}`}
                                alt={u.UserName}
                              />
                            ) : (
                              <span
                                className="user-avatar-fallback"
                                style={{ background: colorForName(u.UserName) }}
                              >
                                {initialsForName(u.UserName)}
                              </span>
                            )}
                            <span className="text-dark fw-semibold">{u.UserName}</span>
                          </div>
                        </td>
                        <td className="px-3 text-center">{u.RoleName || "-"}</td>
                        <td className="px-3 text-center">{formatDateTime(u.CreatedAt)}</td>
                        <td className="px-3 text-center">
                          <span className={`badge px-3 py-2 ${u.Status === "Active" ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>
                            {u.Status}
                          </span>
                        </td>
                        <td className="px-3 text-center">
                          <button
                            type="button"
                            className={`badge px-3 py-2 border-0 ${u.IsEnabled ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleAccessStatus(u)}
                            disabled={togglingId === u.id}
                            title="Click to toggle Enable/Disable"
                          >
                            {togglingId === u.id ? "..." : u.IsEnabled ? "Enabled" : "Disabled"}
                          </button>
                        </td>
                        <td className="px-3 text-end">
                          <div className="btn-group btn-group-sm">
                            <button type="button" className="btn btn-light border" title="View" onClick={() => setViewingUser(u)}>
                              <EyeIcon />
                            </button>
                            <button
                              type="button"
                              className="btn btn-light border"
                              title="Edit"
                              onClick={() => navigate(editUserPath(u.id))}
                            >
                              <EditIcon />
                            </button>
                            {/* Delete a real User account -- Super Admin only per
                                the Role Matrix (Admin's "Account -> Users" cell
                                is Add-Edit, no Delete). Backend enforces this too
                                (require_tier("Super Admin") on DELETE /users/<id>);
                                this just keeps an Admin from seeing a button that
                                would 403 if clicked. */}
                            {canDelete("AccountUsers") && (
                              <button
                                type="button"
                                className="btn btn-light border"
                                title="Delete"
                                onClick={() => handleDelete(u)}
                                disabled={deletingId === u.id}
                              >
                                <TrashIcon />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-footer bg-transparent border-0 pt-3 pb-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <small className="text-muted fw-semibold">
                  {totalEntries === 0
                    ? "Showing 0 entries"
                    : `Showing ${startIndex} to ${endIndex} of ${totalEntries} Users`}
                </small>

                {totalPages > 1 && (
                  <ul className="pagination pagination-sm pagination-boxed mb-0 justify-content-center">
                    <li className={`page-item${currentPage <= 1 ? " disabled" : ""}`}>
                      <button type="button" className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        <i className="ti ti-chevron-left" />
                      </button>
                    </li>
                    {pageNumbers.map((n) => (
                      <li key={n} className={`page-item${n === currentPage ? " active" : ""}`}>
                        <button type="button" className="page-link" onClick={() => setPage(n)}>
                          {n}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item${currentPage >= totalPages ? " disabled" : ""}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        <i className="ti ti-chevron-right" />
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </>
        )}

        {isAccessRequestTab && (
          <>
            <div className="table-responsive">
              {/* users-list-table (new, scoped) -- per request, same
                  treatment as RateCard.jsx/ProjectList.jsx: row background
                  #f2f2f2 @ 38% Transparency, hover #02bc9c @ 95%
                  Transparency, via themeOverrides.css. Applied to both this
                  table and the Access Request tab's table below (same
                  page), rather than changing .table-custom/.table-hover
                  directly, since those are shared by ManageRoles.jsx /
                  RoleMatrix.jsx / AIAssistant.jsx / EmailConfiguration.jsx /
                  ProjectList.jsx and shouldn't change color there too. */}
              <table className="align-middle mb-0 table table-custom table-hover users-list-table text-nowrap w-100">
                <thead className="bg-light border-bottom border-top">
                  <tr className="text-dark fw-bold">
                    <th className="px-3 py-3 text-start">Name</th>
                    <th className="px-3 py-3 text-center">Department</th>
                    <th className="px-3 py-3 text-center">Company</th>
                    <th className="px-3 py-3 text-center">Date Requested</th>
                    <th className="px-3 py-3 text-center">Date Created</th>
                    <th className="px-3 py-3 text-center">Role</th>
                    <th className="px-3 py-3 text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {arLoading ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">Loading...</td>
                    </tr>
                  ) : arPageRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">No access requests.</td>
                    </tr>
                  ) : (
                    arPageRows.map((req) => (
                      <tr key={req.AccessRequestID}>
                        <td className="px-3 text-start">
                          <div className="d-flex align-items-center gap-2 justify-content-start">
                            <span
                              className="user-avatar-fallback"
                              style={{ background: colorForName(req.Name) }}
                            >
                              {initialsForName(req.Name)}
                            </span>
                            <span className="text-dark fw-semibold">{req.Name}</span>
                          </div>
                        </td>
                        <td className="px-3 text-center">{req.Department || "-"}</td>
                        <td className="px-3 text-center">{req.CompanyName || "-"}</td>
                        <td className="px-3 text-center">{formatDateTime(req.RequestedAt)}</td>
                        <td className="px-3 text-center">{formatDateTime(req.DateCreated)}</td>
                        <td className="px-3 text-center">
                          {/* Accepted -- show the role that was assigned when the
                              request was approved (joined server-side from the
                              created Users row). Pending/Rejected/no role: "-". */}
                          {req.RoleName || "-"}
                        </td>
                        <td className="px-3 text-end">
                          <div className="btn-group btn-group-sm">
                            {/* View and Edit both open the same Access Request
                                details page (EditAccessRequestForm.jsx) -- View
                                read-only, Edit already in edit mode -- for every
                                row regardless of current status. That page is
                                also where Accept/Reject now happen (via its
                                Status dropdown), replacing the old inline
                                check/x buttons and Status column here. */}
                            <button
                              type="button"
                              className="btn btn-light border"
                              title="View"
                              onClick={() => navigate(editAccessRequestPath(req.AccessRequestID), { state: { mode: "view" } })}
                            >
                              <EyeIcon />
                            </button>
                            <button
                              type="button"
                              className="btn btn-light border"
                              title="Edit"
                              onClick={() => navigate(editAccessRequestPath(req.AccessRequestID), { state: { mode: "edit" } })}
                            >
                              <EditIcon />
                            </button>
                            <button
                              type="button"
                              className="btn btn-light border"
                              title="Delete"
                              onClick={() => handleDeleteRequest(req)}
                              disabled={arActingId === req.AccessRequestID}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-footer bg-transparent border-0 pt-3 pb-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <small className="text-muted fw-semibold">
                  {arTotalEntries === 0
                    ? "Showing 0 entries"
                    : `Showing ${arStartIndex} to ${arEndIndex} of ${arTotalEntries} entries`}
                </small>

                {arTotalPages > 1 && (
                  <ul className="pagination pagination-sm pagination-boxed mb-0 justify-content-center">
                    <li className={`page-item${arCurrentPage <= 1 ? " disabled" : ""}`}>
                      <button type="button" className="page-link" onClick={() => setArPage((p) => Math.max(1, p - 1))}>
                        <i className="ti ti-chevron-left" />
                      </button>
                    </li>
                    {arPageNumbers.map((n) => (
                      <li key={n} className={`page-item${n === arCurrentPage ? " active" : ""}`}>
                        <button type="button" className="page-link" onClick={() => setArPage(n)}>
                          {n}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item${arCurrentPage >= arTotalPages ? " disabled" : ""}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => setArPage((p) => Math.min(arTotalPages, p + 1))}
                      >
                        <i className="ti ti-chevron-right" />
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {viewingUser && (
        <div className="user-modal-backdrop" onClick={() => setViewingUser(null)}>
          <div className="user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="user-modal-header">
              <h5 className="mb-0">{viewingUser.UserName}</h5>
              <button className="user-modal-close" onClick={() => setViewingUser(null)} aria-label="Close">
                &times;
              </button>
            </div>

            <div className="user-modal-row">
              <span className="user-modal-row-label">Email</span>
              <span className="user-modal-row-value">{viewingUser.Email || "-"}</span>
            </div>
            <div className="user-modal-row">
              <span className="user-modal-row-label">Mobile No.</span>
              <span className="user-modal-row-value">{viewingUser.MobileNo || "-"}</span>
            </div>
            <div className="user-modal-row">
              <span className="user-modal-row-label">Role</span>
              <span className="user-modal-row-value">{viewingUser.RoleName || "-"}</span>
            </div>
            <div className="user-modal-row">
              <span className="user-modal-row-label">Department</span>
              <span className="user-modal-row-value">{viewingUser.DepartmentName || "-"}</span>
            </div>
            <div className="user-modal-row">
              <span className="user-modal-row-label">Company</span>
              <span className="user-modal-row-value">{viewingUser.CompanyName || "-"}</span>
            </div>
            <div className="user-modal-row">
              <span className="user-modal-row-label">Account Status</span>
              <span className="user-modal-row-value">{viewingUser.Status || "-"}</span>
            </div>
            <div className="user-modal-row">
              <span className="user-modal-row-label">Access Status</span>
              <span className="user-modal-row-value">{viewingUser.IsEnabled ? "Enabled" : "Disabled"}</span>
            </div>
            <div className="user-modal-row" style={{ border: "none" }}>
              <span className="user-modal-row-label">Created</span>
              <span className="user-modal-row-value">{formatDateTime(viewingUser.CreatedAt)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Same teal modal convention as ProfilePage.jsx's "Profile updated
          successfully" popup (see the note there) -- used here as a
          confirmation instead of a success message, with Cancel/Delete
          buttons in place of the single OK. Shows the same name + email
          + "cannot be undone" detail the old window.confirm() text had. */}
      {confirmDeleteUser && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ backgroundColor: "#124d52" }}>
              <div className="modal-body text-center py-5">
                <div className="bg-white d-inline-flex rounded-circle align-items-center justify-content-center mb-3" style={{ width: 48, height: 48 }}>
                  <i className="ti ti-trash fs-3" style={{ color: "#124d52" }} />
                </div>
                <h5 className="text-white mb-2">
                  Are you sure you want to remove the user "{confirmDeleteUser.UserName}"?
                </h5>
                <p className="text-white-50 mb-0 fs-13">
                  This action cannot be reversed.
                </p>
              </div>
              <div className="modal-footer border-0 justify-content-center pb-4">
                <button type="button" className="btn btn-outline-light px-4" style={{ color: "#fff" }} onClick={cancelDeleteUser}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger px-4" onClick={confirmDeleteUserAction}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Same teal modal convention as confirmDeleteUser above, used for
          deleting an access request instead of a user account. */}
      {confirmDeleteRequest && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ backgroundColor: "#124d52" }}>
              <div className="modal-body text-center py-5">
                <div className="bg-white d-inline-flex rounded-circle align-items-center justify-content-center mb-3" style={{ width: 48, height: 48 }}>
                  <i className="ti ti-trash fs-3" style={{ color: "#124d52" }} />
                </div>
                <h5 className="text-white mb-2">
                  Remove the access request from "{confirmDeleteRequest.Name}"?
                </h5>
                <p className="text-white-50 mb-0 fs-13">
                  This action cannot be reversed.
                </p>
              </div>
              <div className="modal-footer border-0 justify-content-center pb-4">
                <button type="button" className="btn btn-outline-light px-4" style={{ color: "#fff" }} onClick={cancelDeleteRequest}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger px-4" onClick={confirmDeleteRequestAction}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}